// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Framer Motion Animation Variants & Presets
 *
 * Reusable animation patterns for Undef Engine game UI:
 * - Card interactions (hover, tap, select, drag)
 * - Phase transitions (lobby → game → results)
 * - List animations (player lists, scoreboards)
 * - Modal animations (enter/exit)
 * - Loading states
 * - Celebrations & visual feedback
 */

// ============================================================================
// CARD INTERACTION ANIMATIONS
// ============================================================================

export const cardVariants = {
  // Normal state
  rest: {
    scale: 1,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  // Hover state
  hover: {
    scale: 1.05,
    boxShadow: '0 20px 25px rgba(0, 0, 0, 0.2)',
    transition: { duration: 0.2 },
  },
  // Tap/click state
  tap: {
    scale: 0.95,
    transition: { duration: 0.1 },
  },
  // Selected state
  selected: {
    scale: 1.1,
    boxShadow: '0 0 20px rgba(168, 85, 247, 0.5)',
    borderColor: 'rgb(168, 85, 247)',
    transition: { duration: 0.3 },
  },
};

/**
 * Simple card interaction - use with m.div
 * @example
 * <m.div
 *   initial="rest"
 *   whileHover="hover"
 *   whileTap="tap"
 *   variants={cardVariants}
 *   animate={selected ? "selected" : "rest"}
 * >
 *   <TreatmentCard />
 * </m.div>
 */

// ============================================================================
// PHASE TRANSITION ANIMATIONS
// ============================================================================

export const pageTransition = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.4 },
};

export const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

export const childVariants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: 'easeOut' },
  },
};

/**
 * Stagger animation for multiple children
 * @example
 * <m.div variants={containerVariants} initial="hidden" animate="show">
 *   {items.map((item) => (
 *     <m.div key={item.id} variants={childVariants}>
 *       <Item {...item} />
 *     </m.div>
 *   ))}
 * </m.div>
 */

// ============================================================================
// MODAL ANIMATIONS
// ============================================================================

export const modalVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.15, ease: 'easeIn' },
  },
};

export const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

/**
 * Modal with backdrop animation
 * @example
 * <AnimatePresence>
 *   {isOpen && (
 *     <>
 *       <m.div variants={backdropVariants} onClick={onClose} />
 *       <m.div variants={modalVariants}>
 *         <Modal />
 *       </m.div>
 *     </>
 *   )}
 * </AnimatePresence>
 */

// ============================================================================
// LOADING STATE ANIMATIONS
// ============================================================================

export const skeletonVariants = {
  loading: {
    opacity: [0.6, 1, 0.6],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

export const spinnerVariants = {
  animate: {
    rotate: 360,
    transition: {
      duration: 1,
      repeat: Infinity,
      ease: 'linear',
    },
  },
};

// ============================================================================
// SCORE & NUMBER ANIMATIONS
// ============================================================================

export const scoreFlashVariants = {
  initial: { scale: 1, color: 'rgb(255, 255, 255)' },
  animate: {
    scale: 1.3,
    color: 'rgb(255, 215, 0)', // Gold
    transition: { duration: 0.3, ease: 'easeOut' },
  },
  exit: {
    scale: 1,
    color: 'rgb(255, 255, 255)',
    transition: { duration: 0.2 },
  },
};

export const trophyBounceVariants = {
  animate: {
    y: [0, -15, 0],
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
};

// ============================================================================
// LIST REORDERING ANIMATIONS
// ============================================================================

export const listItemVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: 'easeOut' },
  },
  exit: {
    opacity: 0,
    y: 20,
    transition: { duration: 0.2, ease: 'easeIn' },
  },
};

// ============================================================================
// GESTURE SUPPORT (Mobile & Desktop)
// ============================================================================

export const dragVariants = {
  initial: { opacity: 0.8, scale: 1 },
  whileDrag: {
    opacity: 1,
    scale: 1.05,
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
  },
};

export const tapSpringTransition = {
  type: 'spring',
  stiffness: 300,
  damping: 20,
};

// ============================================================================
// PRESET ANIMATION CONFIGURATIONS
// ============================================================================

/**
 * Quick entrance animation
 * @example animate={{ opacity: 1, y: 0 }} initial={fadeInUpInitial}
 */
export const fadeInUpInitial = { opacity: 0, y: 20 };

/**
 * Quick exit animation
 * @example exit={{ opacity: 0, y: 20, transition: { duration: 0.15 } }}
 */

/**
 * Spring animation for bouncy feel
 * @example transition={{ type: "spring", stiffness: 300, damping: 20 }}
 */
export const springTransition = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 20,
};

/**
 * Smooth easing for UI transitions
 * @example transition={{ duration: 0.3, ease: "easeInOut" }}
 */
export const smoothTransition = {
  duration: 0.3,
  ease: 'easeInOut' as const,
};

/**
 * Quick feedback animation
 * @example transition={{ duration: 0.15, ease: "easeOut" }}
 */
export const quickTransition = {
  duration: 0.15,
  ease: 'easeOut' as const,
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Stagger delay calculator for multi-item animations
 * Respects prefers-reduced-motion for accessibility
 */
export function getStaggerDelay(index: number, baseDelay = 0.1): number {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return prefersReducedMotion ? 0 : index * baseDelay;
}

/**
 * Get animation transition respecting prefers-reduced-motion
 * Returns instant transition if reduced motion is preferred
 */
export function getResponsiveTransition(
  transition: import('framer-motion').Transition
): import('framer-motion').Transition {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    return { duration: 0.01 };
  }
  return transition;
}
